import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { openOrDownloadBlob } from '~/modules/covenants/utils.ts';
import {
  deleteGuaranteesClientDocument,
  downloadGuaranteesClientDocument,
  fetchGuaranteesApplicationFiles,
  uploadGuaranteesClientDocument,
} from '~/store/guarantees/thunks/applicationThunks.ts';
import type { AppDispatch } from '~/store/store.ts';
import {
  CLIENT_STEP2_DOCUMENT_FALLBACK_FILE_NAME,
  CLIENT_STEP2_TEXT,
} from '../constants.ts';
import type { TClientGeneratedDocumentRow, TClientUploadDocumentRow } from '../types.ts';
import {
  getWarrantyStatementDocumentFromApplicationFiles,
  mapApplicationFilesToClientDocumentRows,
} from '../utils.ts';
import { GuaranteesFileType, type TGuaranteesApplicationFile } from '~/store/guarantees/types.ts';

export const useClientStep2Documents = ({
  applicationId,
  contragentId,
  setModalAlert,
  showGuaranteesRequestError,
}: {
  applicationId: string;
  contragentId: string;
  setModalAlert: (value: { text: string }) => void;
  showGuaranteesRequestError: (error: unknown) => void;
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [documents, setDocuments] = useState<TClientUploadDocumentRow[]>([]);
  const [generatedDocument, setGeneratedDocument] = useState<TClientGeneratedDocumentRow | null>(
    null
  );
  const [applicationFiles, setApplicationFiles] = useState<TGuaranteesApplicationFile[]>([]);
  const [isApplicationFilesLoading, setIsApplicationFilesLoading] = useState(false);
  const [hasApplicationFilesLoaded, setHasApplicationFilesLoaded] = useState(false);
  const [uploadingDocumentId, setUploadingDocumentId] = useState<string | null>(null);
  const [downloadingDocumentId, setDownloadingDocumentId] = useState<string | null>(null);
  const [deletingDocumentId, setDeletingDocumentId] = useState<string | null>(null);
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [actionMenuDocumentId, setActionMenuDocumentId] = useState<string | null>(null);

  const refreshApplicationFiles = useCallback(async () => {
    if (!contragentId || !applicationId || applicationId === 'new') {
      setDocuments([]);
      setGeneratedDocument(null);
      setApplicationFiles([]);
      setHasApplicationFilesLoaded(true);

      return;
    }

    setIsApplicationFilesLoading(true);

    try {
      const nextApplicationFiles = await dispatch(
        fetchGuaranteesApplicationFiles({ applicationId })
      ).unwrap();

      setApplicationFiles(nextApplicationFiles);
      setGeneratedDocument(getWarrantyStatementDocumentFromApplicationFiles(nextApplicationFiles));
      setDocuments(mapApplicationFilesToClientDocumentRows(nextApplicationFiles));
      setHasApplicationFilesLoaded(true);
    } finally {
      setIsApplicationFilesLoading(false);
    }
  }, [applicationId, contragentId, dispatch]);

  const handleAddDocument = async (file?: File | null) => {
    if (!file) {
      return;
    }

    const documentId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    setUploadingDocumentId(documentId);

    try {
      const uploadedDocument = await dispatch(
        uploadGuaranteesClientDocument({
          applicationId,
          fileType: GuaranteesFileType.CLIENT_FOUNDING_DOCUMENTS_AND_GOVERNANCE_INFO,
          multipartFile: file,
        })
      ).unwrap();

      setDocuments((currentDocuments) => [
        ...currentDocuments,
        {
          id: uploadedDocument.id,
          applicationFileId: uploadedDocument.id,
          fileName: uploadedDocument.fileName,
        },
      ]);

      await refreshApplicationFiles();
    } catch (error) {
      showGuaranteesRequestError(error);
    } finally {
      setUploadingDocumentId(null);
    }
  };

  const handleActionMenuOpen = (
    event: React.MouseEvent<HTMLButtonElement>,
    documentId: string
  ) => {
    setActionMenuAnchorEl(event.currentTarget);
    setActionMenuDocumentId(documentId);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchorEl(null);
    setActionMenuDocumentId(null);
  };

  const handleDownloadDocument = async (
    document: TClientUploadDocumentRow,
    forceDownload: boolean
  ) => {
    handleActionMenuClose();

    if (!document.fileId) {
      setModalAlert({ text: CLIENT_STEP2_TEXT.missingFileMessage });

      return;
    }

    setDownloadingDocumentId(document.id);

    try {
      const downloadedDocument = await dispatch(
        downloadGuaranteesClientDocument({
          id: document.fileId,
          isAttached: !forceDownload,
        })
      ).unwrap();

      openOrDownloadBlob({
        blob: downloadedDocument.data,
        fileName: document.fileName || downloadedDocument.filename,
        fallbackFileName: CLIENT_STEP2_DOCUMENT_FALLBACK_FILE_NAME,
        contentType: downloadedDocument.contentType,
        forceDownload,
      });
    } catch (error) {
      showGuaranteesRequestError(error);
    } finally {
      setDownloadingDocumentId(null);
    }
  };

  const handleDeleteUploadedDocument = async (document: TClientUploadDocumentRow) => {
    handleActionMenuClose();
    const applicationFileId = document.applicationFileId;

    if (!applicationFileId) {
      setModalAlert({ text: CLIENT_STEP2_TEXT.missingFileMessage });

      return;
    }

    setDeletingDocumentId(document.id);

    try {
      await dispatch(
        deleteGuaranteesClientDocument({
          applicationFileId,
        })
      ).unwrap();

      setDocuments((currentDocuments) =>
        currentDocuments.filter((currentDocument) => currentDocument.id !== document.id)
      );
      await refreshApplicationFiles();
    } catch (error) {
      showGuaranteesRequestError(error);
    } finally {
      setDeletingDocumentId(null);
    }
  };

  useEffect(() => {
    void refreshApplicationFiles().catch(showGuaranteesRequestError);
  }, [refreshApplicationFiles, showGuaranteesRequestError]);

  return {
    documents,
    generatedDocument,
    applicationFiles,
    isApplicationFilesLoading,
    hasApplicationFilesLoaded,
    uploadingDocumentId,
    downloadingDocumentId,
    deletingDocumentId,
    actionMenuAnchorEl,
    actionMenuDocumentId,
    onAddDocument: handleAddDocument,
    onActionMenuOpen: handleActionMenuOpen,
    onActionMenuClose: handleActionMenuClose,
    onDownload: handleDownloadDocument,
    onDeleteUploadedDocument: handleDeleteUploadedDocument,
    refreshApplicationFiles,
  };
};
